import React, { useContext } from 'react';
import styled from 'styled-components';
import { Caption, GetColor, Headline3, Icon, Subtitle1 } from 'venn-ui-kit';
import { analyticsService } from 'venn-utils';
import { DataUploaderMode } from '../types';
import { uploadConfig } from '../utils';
import { getIconForFormat } from './Icons';
import type { Sample } from './SampleData';
import { FileLayout } from './SampleData';
import UniversalUploaderContext from '../../../contexts/universal-uploader';

interface SampleCardProps {
  sample: Sample;
  mode: DataUploaderMode;
}

const SampleCard = ({ sample, mode }: SampleCardProps) => {
  const { refreshedStyling } = useContext(UniversalUploaderContext);
  return (
    <a
      target="_blank"
      href={`/api/file/tag/${sample.tag}`}
      rel="noreferrer"
      onClick={() =>
        analyticsService.fileTemplateDownloaded({
          dataType: uploadConfig[mode]?.dataType,
          isPrivatesTemplate: mode === DataUploaderMode.Privates,
          tag: sample.tag,
        })
      }
    >
      <Container refreshedStyling={refreshedStyling} data-testid="qa-sample-card">
        <IconContainer>{getIconForFormat(sample.layout)}</IconContainer>
        <Details>
          <StyledHeadline>{sample.title}</StyledHeadline>
          <Subtitle1>{sample.description}</Subtitle1>
          <Info>
            {sample.info ? (
              <InfoCaption>
                <Icon prefix="far" type="circle-info" />
                &nbsp;
                {sample.info}
              </InfoCaption>
            ) : (
              <>
                <Caption>
                  <b>Layout: </b>
                  <LayoutIcon
                    type={sample.layout === FileLayout.GRID ? 'th-large' : 'line-columns'}
                    prefix="fad"
                    layout={sample.layout}
                  />{' '}
                  {sample.layout}
                </Caption>
                {sample.frequency && (
                  <Caption>
                    <b>Supported Frequencies: </b> {sample.frequency.join(', ')}
                  </Caption>
                )}
              </>
            )}
          </Info>
        </Details>
        <DownloadIconWrapper refreshedStyling={refreshedStyling}>
          <Icon type="download" /> DOWNLOAD
        </DownloadIconWrapper>
      </Container>
    </a>
  );
};

export default SampleCard;

const Container = styled.div<{ refreshedStyling: boolean }>`
  width: 100%;
  border: 1px solid ${GetColor.GreyScale.Grey40};
  border-radius: 2px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 10px;

  &:hover {
    border: 1px solid ${(props) => (props.refreshedStyling ? GetColor.DarkBlue : GetColor.Primary.Dark)};
  }
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: top;
  align-items: baseline;
  width: 175px;
  min-width: 175px;
  height: auto;
  background-color: ${GetColor.GreyScale.Grey10};
  border-right: 1px solid ${GetColor.GreyScale.Grey40};
  overflow: hidden;
  font-size: 20px;
  padding: 5px;
`;

const Details = styled.div`
  padding: 10px 10px 10px 20px;
  flex: 1;
`;

const Info = styled.div`
  display: flex;
  flex-direction: row;
  gap: 15px;
`;

const LayoutIcon = styled(Icon)<{ layout: FileLayout }>`
  padding: 0 2px;
  ${(props) => props.layout === FileLayout.HORIZONTAL && 'transform: rotate(90deg);'}
`;

const StyledHeadline = styled(Headline3)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
`;

const DownloadIconWrapper = styled.div<{ refreshedStyling: boolean }>`
  display: flex;
  flex-direction: row;
  align-self: center;
  gap: 4px;
  font-weight: bold;
  font-size: 14px;
  width: 120px;
  color: ${(props) => (props.refreshedStyling ? GetColor.DarkBlue : GetColor.Primary.Dark)};
`;

const InfoCaption = styled(Caption)`
  color: ${GetColor.GreyScale.Grey80};
  font-size: 14px;
`;
